import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import * as fabric from 'fabric';
import { Select } from "../../../../form/select";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faComments } from "@fortawesome/free-regular-svg-icons";
import { faCloudArrowUp, faCrop, faCropSimple, faMinus, faPlus, faRotateRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Editor, Main } from "./style";
import { webURL } from "../../../../../services/API";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";

type OCRProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const CANVAS_WIDTH = 833, CANVAS_HEIGHT = 772;

// Reference/annotation fields moved from single values to per-page arrays to
// support multi-page PDF references. Legacy nodes stored a plain string for
// each of these fields, so normalize on read.
const toPageArray = (value: any): string[] => Array.isArray(value) ? value : (value ? [value] : []);
const toNullablePageArray = (value: any): (string | null)[] => Array.isArray(value) ? value : (value ? [value] : []);

export function OCR({ id, data, setChildMinimize, setChildSettings }: OCRProps) {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const { setNodes, getNodes = () => [], getEdges = () => [] } = useReactFlow();
   const [types, setTypes] = React.useState<selectDataType[]>([
      { id: 1, name: 'Image', refId: 'image', isActive: true }, { id: 2, name: 'Document', refId: 'document' }, 
      { id: 3, name: 'Check Id', refId: 'check_id' }, { id: 4, name: 'Annotate', refId: 'annotate' },
   ]);
   const [canvas, setCanvas] = React.useState<fabric.Canvas | null>(null);
   const [annotations, setAnnotations] = React.useState({ isCanDrawing: false, isDrawing: false });
   const [rect, setRect] = React.useState<any>(null);
   const [startX, setStartX] = React.useState(0);
   const [startY, setStartY] = React.useState(0);
   const inputRef = React.useRef<HTMLInputElement>(null);
   const fileInputRef = React.useRef<HTMLInputElement>(null);
   const canvasRef = React.useRef<HTMLCanvasElement>(null);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isReferenceEditorOpen, setIsReferenceEditorOpen] = React.useState(Boolean(data.value?.isAnnotationsToggle));
   const [suiteData, setSuiteData] = React.useState<selectDataType[]>([]);
   const [suiteFiles, setSuiteFiles] = React.useState<selectDataType[]>([]);
   const [suiteLoading, setSuiteLoading] = React.useState(false);
   const [filesLoading, setFilesLoading] = React.useState(false);
   const [suiteError, setSuiteError] = React.useState('');
   const [filesError, setFilesError] = React.useState('');
   const [uploadError, setUploadError] = React.useState('');
   const [isUploading, setIsUploading] = React.useState(false);
   const [annotationList, setAnnotationList] = React.useState<{ id: string; label: string }[]>([]);
   const [activePageIndex, setActivePageIndex] = React.useState(0);
   const [isConvertingPdf, setIsConvertingPdf] = React.useState(false);
   const isReferenceMode = data.value?.type === 'check_id' || data.value?.type === 'annotate';
   const annotationsUrlPages = toPageArray(data.value?.annotationsUrl);
   const canvasJSONPages = toNullablePageArray(data.value?.canvasJSON);
   const canvasURLimgPages = toNullablePageArray(data.value?.canvasURLimg);
   const pageCount = annotationsUrlPages.length;
   const currentPageIndex = Math.min(activePageIndex, Math.max(pageCount - 1, 0));
   const totalAnnotationCount = canvasJSONPages.reduce((sum, pageJson) => {
      if (!pageJson) return sum;
      try {
         const objects = JSON.parse(pageJson)?.objects;
         return sum + (Array.isArray(objects) ? objects.filter((o: any) => o.type === 'rect' && o.label).length : 0);
      } catch {
         return sum;
      }
   }, 0);
   const connectedInputSources = getEdges()
      .filter(edge => edge.target === id)
      .map(edge => getNodes().find(node => node.id === edge.source))
      .filter((source): source is NonNullable<typeof source> => Boolean(source))
      .map(source => {
         const value = source.data?.value;
         const fileValues = [
            ...(Array.isArray(value?.files) ? value.files : []),
            ...(Array.isArray(value?.current) ? value.current : []),
         ];
         const fileNames = fileValues
            .map(file => file?.name || file?.fileName)
            .filter((name): name is string => typeof name === 'string' && Boolean(name.trim()));
         const hasDirectInput = source.data?.action === 'input' && (
            (typeof value === 'string' && Boolean(value.trim())) ||
            Boolean(value?.url || value?.fileURL || value?.fileUrl)
         );
         return {
            name: source.data?.action === 'input' ? 'Image input' : source.data?.name || source.data?.action || 'Workflow input',
            fileNames,
            hasFile: fileNames.length > 0 || hasDirectInput,
         };
      })
      .filter(source => source.hasFile);

   // ---
   React.useEffect(() => {
      let isMounted = true;
      setSuiteLoading(true);
      setSuiteError('');
      (async function() {
         try {
            const info = (await axios.get(`${webURL}/ocr?type=suiteWorkflow`)).data;
            if (!isMounted) return;
            setSuiteData(info.map((item: any) => ({ id: item._id, name: item.name, isActive: data.value?.suiteId === item._id })));
         } catch {
            if (isMounted) setSuiteError('Unable to load OCR suites');
         } finally {
            if (isMounted) setSuiteLoading(false);
         }
      })();
      return () => { isMounted = false };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      const suiteId = data.value?.suiteId;
      if (!suiteId) {
         setSuiteFiles([]);
         setFilesError('');
         setFilesLoading(false);
         return;
      }

      let isMounted = true;
      setFilesLoading(true);
      setFilesError('');
      setSuiteFiles([]);
      (async function() {
         try {
            const info = (await axios.get(`${webURL}/ocr?type=suiteFiles&suiteId=${encodeURIComponent(suiteId)}`)).data;
            if (!isMounted) return;
            setSuiteFiles(info.map((item: any) => ({ id: item._id, name: item.fileName, refId: item.fileURL, isActive: data.value?.suiteFile?.id === item._id })));
         } catch {
            if (isMounted) setFilesError('Unable to load files for this suite');
         } finally {
            if (isMounted) setFilesLoading(false);
         }
      })();
      return () => { isMounted = false };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.suiteId]);
      
   // --
   React.useEffect(() => {
      if (setChildMinimize) {
         setChildMinimize(() => {
            setIsExpanded(prev => !prev);
         });
      }
      if (setChildSettings) {
         setChildSettings(() => {
            setSettings(prev => ({ ...prev, isToggle: !prev.isToggle }));
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            type: types[0].refId,
            annotationsLabel: 'Unlabeled',
            isNoMinimize: false,
            isAnnotationsToggle: false,
            zoom: 1,
         });
      } else {
         if (data.value.isNoMinimize) updateNodeValue(id, setNodes, { isNoMinimize: false, isAnnotationsToggle: false });
         setTypes(types => types.map(type => ({ ...type, isActive: type.refId === data.value.type })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   React.useEffect(() => {
      if (!isExpanded || !isReferenceEditorOpen || !isReferenceMode || !canvasRef.current) return;
      const initCanvas = new fabric.Canvas(canvasRef.current, { width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
      canvasRef.current.width = CANVAS_WIDTH;
      canvasRef.current.height = CANVAS_HEIGHT;
      setCanvas(initCanvas);
      return () => {
         initCanvas.dispose();
         setCanvas(null);
      };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isExpanded, isReferenceEditorOpen, isReferenceMode]);
   // --- Load the active page's reference image + annotations into the canvas
   // whenever the canvas is (re)created or the user switches pages.
   React.useEffect(() => {
      if (!canvas || !data.value?.isAnnotationsFile) return;
      if (!pageCount) {
         updateNodeValue(id, setNodes, { canvasJSON: [], annotationsUrl: [], isAnnotationsFile: false, canvasURLimg: [] });
         setAnnotationList([]);
         return;
      }
      canvas.clear();
      const pageRefUrl = annotationsUrlPages[currentPageIndex];
      const pageJSON = canvasJSONPages[currentPageIndex];
      if (!pageRefUrl) {
         setAnnotationList([]);
         return;
      }
      if (pageJSON) {
         canvas.loadFromJSON(pageJSON, () => {
            canvas.renderAll();
            setImageTocanvas(pageRefUrl);
            const labels: { id: string; label: string }[] = [];
            canvas.getObjects().forEach((obj: any, idx: number) => {
               if (obj.type === 'rect' && obj.label) labels.push({ id: `${obj.label}-${idx}`, label: obj.label });
            });
            setAnnotationList(labels);
         });
      } else {
         setImageTocanvas(pageRefUrl);
         setAnnotationList([]);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas, currentPageIndex, data.value?.isAnnotationsFile, data.value?.canvasJSON, data.value?.annotationsUrl]);

   // ---
   React.useEffect(() => {
      if (canvas && data.value?.annotationsPassingUrl && data.value.annotationsPassingUrl !== annotationsUrlPages[0]) {
         setActivePageIndex(0);
         setImageTocanvas(data.value.annotationsPassingUrl);
         updateNodeValue(id, setNodes, { annotationsPassingUrl: '', annotationsUrl: [data.value.annotationsPassingUrl], canvasJSON: [], canvasURLimg: [], isAnnotationsFile: true });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas, data.value?.annotationsPassingUrl]);

   // ---
   React.useEffect(() => {
      if (canvas) {
         canvas.on('mouse:down', startDrawing);
         canvas.on('mouse:move', drawRectangle);
         canvas.on('mouse:up', stopDrawing);
         // Properly cleanup the event listeners
         return () => {
            canvas.off('mouse:down', startDrawing);
            canvas.off('mouse:move', drawRectangle);
            canvas.off('mouse:up', stopDrawing);
         };
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas, startX, startY, rect, annotations]);
   
   // ---
   const startDrawing = (o: any) => {
      if (!canvas || !annotations.isCanDrawing) return;
      setAnnotations(prev => ({ ...prev, isDrawing: true }));
      const pointer = canvas.getPointer(o.e);
      setStartX(pointer.x);
      setStartY(pointer.y);
      const newRect = new fabric.Rect({ 
         left: pointer.x, top: pointer.y, width: 0, height: 0, fill: 'transparent', stroke: '#1E272E', strokeWidth: 2,
         lockScalingX: true, lockScalingY: true, lockRotation: true, hasControls: false,
      });
      setRect(newRect);
      canvas.add(newRect);
   }
   const drawRectangle = (o: any) => {
      if (!annotations.isDrawing || !rect || !canvas) return;
      const pointer = canvas.getPointer(o.e);
      const updatedRect = { ...rect };
   
      if (startX > pointer.x) updatedRect.left = pointer.x;
      if (startY > pointer.y) updatedRect.top = pointer.y;
   
      updatedRect.width = Math.abs(startX - pointer.x);
      updatedRect.height = Math.abs(startY - pointer.y);
      // Instead of spreading the updatedRect, update each property individually
      rect.set({ left: updatedRect.left, top: updatedRect.top, width: updatedRect.width, height: updatedRect.height });
      canvas.renderAll();
   }
   const stopDrawing = async () => {
      if (!annotations.isDrawing) return;
      setAnnotations(prev => ({ ...prev, isDrawing: false, isCanDrawing: false }));
      if (!data.value.annotationsLabel || typeof data.value.annotationsLabel !== 'string') return;

      // Ensure unique label so multiple annotations are preserved
      let label = data.value.annotationsLabel.trim();
      const existingLabels = new Set(annotationList.map(item => item.label));
      if (existingLabels.has(label)) {
         let suffix = 2;
         while (existingLabels.has(`${label}_${suffix}`)) suffix++;
         label = `${label}_${suffix}`;
      }

      const textLeft = rect.left;
      const textTop = rect.top - 35;
      const backgroundRect = new fabric.Rect({
         left: textLeft - 5, top: textTop - 5, width: label.length * 12, 
         height: 33, fill: 'rgb(30, 39, 46)', stroke: 'transparent', rx: 10, ry: 10,
      });
      const text = new fabric.Text(label, { left: textLeft, top: textTop, fontSize: 20, fill: '#fff' });
      if (canvas) {
         const newId = `${label}-${Date.now()}`;
         const group = new fabric.Group([backgroundRect, text], { left: textLeft - 5, top: textTop - 5 });
         (group as any).annotationId = newId;
         canvas.add(group);
         rect.set('label', label);
         (rect as any).annotationId = newId;

         const originalToObject = rect.toObject;
         rect.toObject = function() {
            return Object.assign(originalToObject.call(this), {
               label: this.get('label'),
               annotationId: (rect as any).annotationId,
            });
         };
         canvas.forEachObject(obj => obj.set({ hasControls: false }));
         canvas.renderAll();

         setAnnotationList(prev => [...prev, { id: newId, label }]);
         updateNodeValue(id, setNodes, { annotationsLabel: '' });

         // @ts-ignore
         await getURLfromBase64(document.querySelector(`#${data.index}`)?.toDataURL(), data.index).then(url => {
            const nextCanvasJSON = [...canvasJSONPages];
            const nextCanvasURLimg = [...canvasURLimgPages];
            nextCanvasJSON[currentPageIndex] = JSON.stringify(canvas.toJSON());
            nextCanvasURLimg[currentPageIndex] = url;
            updateNodeValue(id, setNodes, { canvasJSON: nextCanvasJSON, canvasURLimg: nextCanvasURLimg });
         });
      } 
   }

   // ---
   const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      if (!target.files || !target.files[0]) return;
      const file = target.files[0];
      setIsUploading(true);
      setUploadError('');
      try {
         const formData = new FormData();
         formData.append('file', file);
         const url = (await axios.post(`${webURL}/general/uploader`, formData)).data.url;
         const newFile = { id: `uploaded-${Date.now()}`, name: file.name, refId: url, isActive: true, isTemporary: true };
         setSuiteFiles(prev => prev.map(f => ({ ...f, isActive: false })).concat(newFile));
         updateNodeValue(id, setNodes, { suiteFile: newFile });
      } catch {
         setUploadError('Unable to upload the test file');
      } finally {
         setIsUploading(false);
         target.value = '';
      }
   }

   // ---
   const handleInputImageChange = async (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      setUploadError('');

      try {
         const request = new FormData();
         request.append('file', file);
         const response = (await axios.post(`${webURL}/general/uploader`, request, { headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': userToken }})).data;

         if (isPdf) {
            // Rasterize every page so each one gets its own annotation canvas.
            setIsConvertingPdf(true);
            const conversion = (await axios.post(`${webURL}/other/pdfToImg`, { url: response.url, allPages: true })).data;
            const pageUrls: string[] = conversion.fileURLs?.length ? conversion.fileURLs : (conversion.fileURL ? [conversion.fileURL] : []);
            if (!pageUrls.length) throw new Error('No pages found in PDF');

            setActivePageIndex(0);
            updateNodeValue(id, setNodes, {
               annotationsUrl: pageUrls,
               canvasJSON: pageUrls.map(() => null),
               canvasURLimg: pageUrls.map(() => null),
               isAnnotationsFile: true,
            });
         } else {
            setActivePageIndex(0);
            setImageTocanvas(response.url, () => {
               updateNodeValue(id, setNodes, {
                  annotationsUrl: [response.url],
                  canvasJSON: [null],
                  canvasURLimg: [null],
                  isAnnotationsFile: true,
               });
            });
         }
      } catch {
         setUploadError(isPdf ? 'Unable to convert the PDF reference' : 'Unable to upload the reference image');
      } finally {
         setIsConvertingPdf(false);
         target.value = '';
      }
   }

   // ---
   const setImageTocanvas = (source: string, callback?: () => void) => {
      const imgObj = new Image();
      imgObj.crossOrigin = 'Anonymous';
      imgObj.src = source;

      imgObj.onload = () => {
         const image = new fabric.Image(imgObj);
         if (!canvas) return;
         const imageAspectRatio = imgObj.width / imgObj.height;
         const canvasAspectRatio = canvas.width / canvas.height;
         let scaleFactor;
         if (canvasAspectRatio > imageAspectRatio) scaleFactor = canvas.height / imgObj.height;
         else scaleFactor = canvas.width / imgObj.width;
         
         image.scale(scaleFactor);
         image.set({ left: (canvas.width - image.getScaledWidth()) / 2, top: (canvas.height - image.getScaledHeight()) / 2 });
         canvas.backgroundImage = image;
         canvas.renderAll();
         if (callback) callback();
      }
   }

   // ---
   const deleteAnnotation = async (label: string) => {
      if (!canvas) return;
      const objects = canvas.getObjects();
      const toRemove = objects.filter((obj: any) => {
         if (obj.type === 'rect' && obj.label === label) return true;
         if (obj.type === 'group') {
            return obj._objects?.some((child: any) => child.type === 'text' && child.text === label);
         }
         return false;
      });
      toRemove.forEach((obj: any) => canvas.remove(obj));
      canvas.renderAll();
      setAnnotationList(prev => prev.filter(item => item.label !== label));
      // @ts-ignore
      const url = await getURLfromBase64(document.querySelector(`#${data.index}`)?.toDataURL(), data.index);
      const nextCanvasJSON = [...canvasJSONPages];
      const nextCanvasURLimg = [...canvasURLimgPages];
      nextCanvasJSON[currentPageIndex] = JSON.stringify(canvas.toJSON());
      nextCanvasURLimg[currentPageIndex] = url;
      updateNodeValue(id, setNodes, { canvasJSON: nextCanvasJSON, canvasURLimg: nextCanvasURLimg });
   }

   // --- Clear the active page's regions, or (isWithImage) reset the whole
   // multi-page reference back to empty so a fresh document can be uploaded.
   const clearAllAnnotations = (isWithImage = false) => {    
      if (!canvas) return;  
      setAnnotationList([]);
      if (!isWithImage) {
         const objects = canvas.getObjects();
         const rectangles = objects.filter(obj => obj.type === 'rect');
         const groups = objects.filter(obj => obj.type === 'group');
         rectangles.forEach(rect => canvas.remove(rect));
         groups.forEach(group => canvas.remove(group));
         setRect(null);
         canvas.renderAll();
         const nextCanvasJSON = [...canvasJSONPages];
         nextCanvasJSON[currentPageIndex] = null;
         updateNodeValue(id, setNodes, { canvasJSON: nextCanvasJSON });
      } else {
         if (inputRef.current) inputRef.current.value = '';
         setActivePageIndex(0);
         updateNodeValue(id, setNodes, { canvasJSON: [], canvasURLimg: [], isAnnotationsFile: false, annotationsUrl: [] });
         canvas.clear(); 
         canvas.renderAll();
      }
   }
   // ---
   const zoomToPoint = (newZoom: number) => {
      if (!canvas) return;
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const centerPoint = new fabric.Point(canvasWidth / 2, canvasHeight / 2);
  
      canvas.zoomToPoint(centerPoint, newZoom);
      updateNodeValue(id, setNodes, { zoom: newZoom });
   }

   // ---
   const handleSelectTypes = (selectId: number) => {
      setTypes((types) => types.map((type) => ({ ...type, isActive: type.id === selectId })));
      const refId = types.find(obj => obj.id === selectId)?.refId;
      updateNodeValue(id, setNodes, { type: refId, isAnnotationsToggle: false });
      setIsReferenceEditorOpen(false);
   }

   // ---
   const handleSelectSuite = async (type: 'suit' | 'file', selectId: string) => {
      if (type === 'suit') {
         setSuiteData((suits) => suits.map((suit) => ({ ...suit, isActive: suit.id === selectId })));
         setSuiteFiles([]);
         setFilesError('');
         updateNodeValue(id, setNodes, { suiteId: selectId, suiteFile: null });

      } else if (type === 'file') {
         setSuiteFiles((files) => files.map((file) => ({ ...file, isActive: file.id === selectId })));
         const file = suiteFiles.find(obj => obj.id === selectId);
         updateNodeValue(id, setNodes, { suiteFile: file || null });
      }
   }

   // ---
   const selectedFile = data.value?.suiteFile;
   const selectedFileName = selectedFile?.name || 'No file selected';
   const operationLabel = types.find(type => type.refId === data.value?.type)?.name || 'Image';
   const hasConnectedInput = connectedInputSources.length > 0;
   const hasInputSource = Boolean(selectedFile || hasConnectedInput);
   const isWarning = !hasInputSource || (isReferenceMode && (!pageCount || totalAnnotationCount === 0));
   const connectedInputSummary = connectedInputSources.map(source => {
      const names = source.fileNames.slice(0, 2).join(', ');
      return names ? `${source.name}: ${names}` : source.name;
   }).join(' · ');
   const sourceSummary = selectedFile?.isTemporary
      ? 'Temporary test file'
      : selectedFile
         ? selectedFileName
         : hasConnectedInput
            ? `Connected: ${connectedInputSummary}`
            : 'Connect a File Uploader or image input';

   const clearSelectedFile = () => {
      setSuiteFiles(files => files.map(file => ({ ...file, isActive: false })));
      updateNodeValue(id, setNodes, { suiteFile: null });
   };

   const toggleReferenceEditor = () => {
      const next = !isReferenceEditorOpen;
      setIsReferenceEditorOpen(next);
      updateNodeValue(id, setNodes, { isAnnotationsToggle: next });
   };

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      {isWarning ? <>
         <div id="warning"></div>
         <span id="warningIcon"><IoWarningOutline size={21} /></span>
      </> : data.output ? <>
         <div id="ran"></div>
         <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
      </> : null}
      <Main $isNoMinimize={ false }>
         <div className="summary" title={ sourceSummary }>
            <div className="summaryTop">
               <span className="summaryMode">{ operationLabel }</span>
               <span className={ `summaryStatus ${isWarning ? 'warning' : 'ready'}` }>{ isWarning ? 'Needs setup' : 'Ready' }</span>
            </div>
            <span className="summaryFile">{ sourceSummary }</span>
         </div>
      </Main>
      {isExpanded ? <ExpandedBlock
         data={{ id, ...data }}
         handleClose={() => {
            setIsExpanded(false);
            setIsReferenceEditorOpen(false);
         }}
         isWarning={isWarning}
         style={{ width: 'min(760px, calc(100vw - 32px))' }}
      >
         <Editor className="nodrag">
            <div className="editorHeader">
               <div>
                  <span className="eyebrow">OCR configuration</span>
                  <h3>{ operationLabel }</h3>
                  <p>Extract text or structured fields from workflow files.</p>
               </div>
               <span className={ `editorStatus ${isWarning ? 'warning' : 'ready'}` }>{ isWarning ? 'Incomplete setup' : 'Ready to run' }</span>
            </div>

            <section className="section">
               <div className="sectionHeading">
                  <div>
                     <span className="sectionEyebrow">01</span>
                     <h4>What should OCR do?</h4>
                  </div>
                  <span>Choose the extraction mode</span>
               </div>
               <div className="operationGrid">
                  {types.map(type => (
                     <button
                        key={ type.id }
                        type="button"
                        className={ `operationOption ${type.isActive ? 'active' : ''}` }
                        onClick={ _ => handleSelectTypes(type.id as number) }
                     >
                        <span className="operationOptionTitle">{ type.name === 'Check Id' ? 'Verify ID' : type.name === 'Annotate' ? 'Extract labeled fields' : type.name === 'Image' ? 'Read text from images' : 'Read documents' }</span>
                        <span>{ type.name === 'Check Id' ? 'Compare fields against a reference' : type.name === 'Annotate' ? 'Read named regions from a template' : type.name === 'Image' ? 'General image text extraction' : 'Text extraction from documents' }</span>
                     </button>
                  ))}
               </div>
            </section>

            <section className="section">
               <div className="sectionHeading">
                  <div>
                     <span className="sectionEyebrow">02</span>
                     <h4>Choose input files</h4>
                  </div>
                  <span>Connected files can also be used at runtime</span>
               </div>
               <div className="sourceGrid">
                  <Select
                     title="OCR Suite"
                     data={ suiteData }
                     handleSelectItem={ selectedId => handleSelectSuite('suit', selectedId) }
                     isSmall={ true }
                     isLoading={ suiteLoading }
                     errorMsg={ suiteError }
                     emptyMsg="No OCR suites found"
                     menuMinWidth={ 280 }
                  />
                  <Select
                     title="Suite file"
                     data={ suiteFiles }
                     handleSelectItem={ selectedId => handleSelectSuite('file', selectedId) }
                     isSmall={ true }
                     isLoading={ filesLoading }
                     errorMsg={ filesError }
                     emptyMsg={ data.value?.suiteId ? 'No files in this suite' : 'Select a suite first' }
                     menuMinWidth={ 360 }
                  />
               </div>
               <div className={ `selectedFileCard ${hasConnectedInput && !selectedFile ? 'connected' : ''}` }>
                  <div className="fileIcon"><FontAwesomeIcon icon={ selectedFile || hasConnectedInput ? faCheckCircle : faCloudArrowUp } /></div>
                  <div className="fileMeta">
                     <span className="fileLabel">{ selectedFile ? 'Selected OCR input' : hasConnectedInput ? 'Connected workflow input' : 'No file selected' }</span>
                     <span className="fileName" title={ selectedFile?.name || connectedInputSummary }>{ selectedFile ? selectedFileName : hasConnectedInput ? connectedInputSummary : 'Connect a File Uploader or image input' }</span>
                     <span className="fileHint">{ selectedFile?.isTemporary ? 'Temporary upload for this node' : selectedFile ? 'Optional suite file; connected workflow files are also supported' : hasConnectedInput ? 'OCR will use the connected file when this node runs' : 'Select a suite file, upload a test file, or connect an image/file source' }</span>
                  </div>
                  { selectedFile ? <button type="button" className="iconButton" onClick={ clearSelectedFile } title="Remove selected file" aria-label="Remove selected file"><FontAwesomeIcon icon={ faTimes } /></button> : null }
               </div>
               <div className="sourceActions">
                  <input ref={ fileInputRef } type="file" accept="image/*,application/pdf,.pdf,.doc,.docx" onChange={ handleFileUpload } style={{ display: 'none' }} />
                  <button type="button" className="secondaryButton" onClick={ _ => fileInputRef.current?.click() } disabled={ isUploading }>
                     <FontAwesomeIcon icon={ faCloudArrowUp } />
                     <span>{ isUploading ? 'Uploading...' : 'Upload test file' }</span>
                  </button>
                  <span>Uploads are temporary and do not create a suite record.</span>
               </div>
               {uploadError ? <div className="inlineError">{ uploadError }</div> : null}
            </section>

            {isReferenceMode ? <section className={ `section referenceSection ${isReferenceEditorOpen ? 'open' : ''}` }>
               <div className="sectionHeading">
                  <div>
                     <span className="sectionEyebrow">03</span>
                     <h4>Reference template</h4>
                  </div>
                  <button type="button" className="secondaryButton" onClick={ toggleReferenceEditor }>
                     <FontAwesomeIcon icon={ data.value?.isAnnotationsFile ? faCheckCircle : faCropSimple } />
                     <span>{ isReferenceEditorOpen ? 'Hide editor' : 'Set up template' }</span>
                  </button>
               </div>
               <div className="requirements">
                  <span className={ pageCount > 0 ? 'complete' : '' }><FontAwesomeIcon icon={ pageCount > 0 ? faCheckCircle : faComments } /> Reference { pageCount > 1 ? `document (${pageCount} pages)` : 'image' }</span>
                  <span className={ totalAnnotationCount > 0 ? 'complete' : '' }><FontAwesomeIcon icon={ totalAnnotationCount > 0 ? faCheckCircle : faComments } /> At least one labeled region</span>
               </div>
               {isReferenceEditorOpen ? <div className="annotationEditor">
                  <div className="annotationToolbar">
                     <label className="uploadButton">
                        <input ref={ inputRef } type="file" accept="image/*,application/pdf,.pdf" onChange={ handleInputImageChange } />
                        <FontAwesomeIcon icon={ faCloudArrowUp } />
                        <span>{ isConvertingPdf ? 'Converting PDF...' : data.value?.isAnnotationsFile ? 'Replace document' : 'Upload reference' }</span>
                     </label>
                     { data.value?.isAnnotationsFile ? <>
                        <input className="annotationLabelInput" placeholder="Region label" value={ data.value?.annotationsLabel || '' } onChange={ e => updateNodeValue(id, setNodes, { annotationsLabel: e.target.value }) } />
                        <button type="button" className={ `toolButton ${!annotations.isCanDrawing ? 'disabled' : ''}` } onClick={ _ => setAnnotations(prev => ({ ...prev, isCanDrawing: !prev.isCanDrawing })) } title="Draw region" aria-label="Draw region" style={{ backgroundColor: data.background }}><FontAwesomeIcon icon={ faCropSimple } /></button>
                        <button type="button" className="toolButton" onClick={ _ => clearAllAnnotations() } title="Delete all regions on this page" aria-label="Delete all regions on this page" style={{ backgroundColor: data.background }}><FontAwesomeIcon icon={ faCrop } /></button>
                        <button type="button" className="toolButton" onClick={ _ => zoomToPoint((data.value?.zoom || canvas?.getZoom() || 1) + 0.1) } title="Zoom in" aria-label="Zoom in" style={{ backgroundColor: data.background }}><FontAwesomeIcon icon={ faPlus } /></button>
                        <button type="button" className="toolButton" onClick={ _ => {
                           const newZoom = (data.value?.zoom || canvas?.getZoom() || 1) - 0.1;
                           if (newZoom > 0) zoomToPoint(newZoom);
                        }} title="Zoom out" aria-label="Zoom out" style={{ backgroundColor: data.background }}><FontAwesomeIcon icon={ faMinus } /></button>
                        <button type="button" className="toolButton" onClick={ _ => clearAllAnnotations(true) } title="Reset reference" aria-label="Reset reference" style={{ backgroundColor: data.background }}><FontAwesomeIcon icon={ faRotateRight } /></button>
                     </> : null }
                  </div>
                  {uploadError ? <div className="inlineError">{ uploadError }</div> : null}
                  { pageCount > 1 ? <div className="pageTabs">
                     {annotationsUrlPages.map((_, pageIdx) => {
                        let pageRegionCount = 0;
                        try {
                           const objects = canvasJSONPages[pageIdx] ? JSON.parse(canvasJSONPages[pageIdx] as string)?.objects : [];
                           pageRegionCount = Array.isArray(objects) ? objects.filter((o: any) => o.type === 'rect' && o.label).length : 0;
                        } catch { pageRegionCount = 0; }
                        return (
                           <button
                              key={ pageIdx }
                              type="button"
                              className={ `pageTab ${pageIdx === currentPageIndex ? 'active' : ''}` }
                              onClick={ _ => setActivePageIndex(pageIdx) }
                           >
                              <span>Page { pageIdx + 1 }</span>
                              { pageRegionCount > 0 ? <span className="pageTabBadge">{ pageRegionCount }</span> : null }
                           </button>
                        );
                     })}
                  </div> : null }
                  <div className="annotationLayout">
                     <div className="canvasViewport">
                        <canvas id={ data.index } ref={ canvasRef } />
                        { !data.value?.isAnnotationsFile ? <div className="canvasEmpty"><FontAwesomeIcon icon={ faCloudArrowUp } /><span>Upload a reference image or PDF to define regions.</span></div> : null }
                     </div>
                     <div className="annotationList">
                        <div className="annotationListHeader"><span>Regions{ pageCount > 1 ? ` (Page ${currentPageIndex + 1})` : '' }</span><span>{ annotationList.length }</span></div>
                        {annotationList.length ? annotationList.map(item => (
                           <div className="annotationItem" key={ item.id }>
                              <span title={ item.label }>{ item.label }</span>
                              <button type="button" onClick={ _ => deleteAnnotation(item.label) } title={ `Delete ${item.label}` } aria-label={ `Delete ${item.label}` }><FontAwesomeIcon icon={ faTimes } /></button>
                           </div>
                        )) : <span className="annotationEmpty">No regions yet</span>}
                     </div>
                  </div>
               </div> : null}
            </section> : null}

            <section className="section outputSection">
               <div className="sectionHeading">
                  <div>
                     <span className="sectionEyebrow">{ isReferenceMode ? '04' : '03' }</span>
                     <h4>Outputs</h4>
                  </div>
                  <span>Choose an output port when connecting the next node</span>
               </div>
               <div className="outputGrid">
                  <div><strong>Extracted text</strong><span>Output-Full</span></div>
                  <div><strong>Processed file</strong><span>Output-Processed-Files</span></div>
               </div>
            </section>
         </Editor>
      </ExpandedBlock> : null}
   </>);
}

// ---
async function getURLfromBase64(data: string, index: string) {
   const base64Response = await fetch(data);
   const blob = await base64Response.blob();
   
   const request = new FormData();
   request.append('file', blob, `${index}.png`);
   const response = (await axios.post(`${webURL}/general/uploader`, request)).data;
   return response.url;
}